// Mini modal with picture having a gray background around it (two "blocks")
import React, { useContext, useEffect, useRef } from 'react';
import { TinyColor } from '@ctrl/tinycolor';
import { css, useTheme } from '@emotion/react';
import {
  EditOfferPageContext,
  PreviewDevice,
} from '@bbkAdminComponents/experiences/offers/edit-offer-page-context';
import { MobileDevicePreview } from '@bbkAdminComponents/preview-device/mobile-device';
import {
  type HeroImageV0,
  type HeroImageV1,
  type RestFortySixtyMiniModal2Modal,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { RefsContext } from '@bbkFunnelUtils/refs.context';
import {
  Avatar,
  CloseOfferButton,
  ModalDesktopInnerSpacer,
  ModalMobileSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '../sections';

type Props = {
  modalInfo: RestFortySixtyMiniModal2Modal;
  close: () => void;
  detailedAction: React.ReactElement;
};

export const FortySixtyMiniModal2V2: React.FC<Props> = ({
  modalInfo: {
    id,
    requires: {
      info: { title, details },
      person,
      hero,
    },
    mobileLayout,
  },
  close,
  detailedAction,
}) => {
  const { mobileView } = useMediaQueries();
  const theme = useTheme() as EmotionTheme;
  const backgroundColor = new TinyColor(theme.modalBackground)
    .darken(4)
    .toHexString();

  const offerContext = useContext(EditOfferPageContext);

  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const titleRef = useRef<HTMLSpanElement | HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-title`, titleRef), [titleRef]);
  const personRef = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-person`, personRef),
    [personRef]
  );
  const descRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-desc`, descRef), [descRef]);
  const actionsRef = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-actions`, actionsRef),
    [actionsRef]
  );

  const personOrImage = () => {
    if (hero) {
      return (
        <img
          alt=""
          src={(hero as HeroImageV0).img || (hero as HeroImageV1).url}
          css={css`
            height: 100%;
            width: 100%;
            object-fit: contain;
          `}
        />
      );
    }
    return <Avatar person={person} vertical />;
  };

  const mobileViewContent = (
    <div className="forty-sixty-mini-2 display-grid mobile group-container">
      <div
        css={css`
          background: ${backgroundColor};
          padding: 12px 1rem 0;
        `}
        className="display-grid mobile content-container align-items--center"
      >
        <OfferTitleMobile
          ref={titleRef as React.RefObject<HTMLDivElement>}
          title={<TitleTheme title={title} />}
          close={close}
        />
        <div ref={personRef}>{personOrImage()}</div>
      </div>
      <ModalMobileSpacer className="forty-sixty-mini-2 display-grid mobile inner-spacer">
        <div ref={descRef}>
          <CustomerHtml value={details} />
        </div>
        <div ref={actionsRef}>{detailedAction}</div>
      </ModalMobileSpacer>
    </div>
  );

  if (offerContext && offerContext?.previewDevice === PreviewDevice.MOBILE) {
    return (
      <MobileDevicePreview mobileLayout={mobileLayout}>
        {mobileViewContent}
      </MobileDevicePreview>
    );
  }

  if (mobileView) return mobileViewContent;

  return (
    <ModalDesktopInnerSpacer
      css={css`
        max-width: 800px;
        padding: 0;
      `}
      className="forty-sixty-mini-2 display-grid desktop inner-spacer"
    >
      <div
        css={css`
          background: ${backgroundColor};
          display: grid;
          place-items: center;
        `}
        className="display-grid place-items--center"
        ref={personRef}
      >
        {personOrImage()}
      </div>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          padding: 1rem 1.5rem;
        `}
      >
        <CloseOfferButton onClick={close} className="align-self--flex-end" />
        <div
          css={css`
            overflow-y: auto;
            flex: 1;
          `}
          className="display-grid place-items--center"
        >
          <div>
            <TitleTheme
              title={title}
              css={{ marginBottom: '1rem' }}
              ref={titleRef}
            />
            <div ref={descRef}>
              <CustomerHtml value={details} />
            </div>
          </div>
        </div>
        <div ref={actionsRef}>{detailedAction}</div>
      </div>
    </ModalDesktopInnerSpacer>
  );
};
